import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, AlertTriangle, ChevronRight, ChevronDown, X, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { priceHistoryAPI } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import SupplierProductPreviewDialog from '@/components/SupplierProductPreviewDialog';

/**
 * Alert card shown on the dashboard.
 * Lists supplier products whose last price variation exceeds the given threshold.
 *
 * Props:
 *  - defaultThreshold: number — initial variation threshold in percent (default 3)
 *  - maxItems: number — max alerts to show collapsed (default 5)
 */
export default function PriceAlertsCard({ defaultThreshold = 3, maxItems = 5 }) {
    const [threshold, setThreshold] = useState(() => {
        const stored = parseFloat(localStorage.getItem('priceAlertsThreshold'));
        return isFinite(stored) && stored > 0 ? stored : defaultThreshold;
    });
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(false);
    // Collapsible card body (collapsed by default to declutter the dashboard)
    const [bodyOpen, setBodyOpen] = useState(() => {
        const raw = localStorage.getItem('priceAlertsBodyOpen');
        return raw === null ? false : raw === '1';
    });
    useEffect(() => {
        localStorage.setItem('priceAlertsBodyOpen', bodyOpen ? '1' : '0');
    }, [bodyOpen]);
    const [dismissed, setDismissed] = useState(() => {
        try {
            return new Set(JSON.parse(localStorage.getItem('dismissedPriceAlerts') || '[]'));
        } catch { return new Set(); }
    });
    const [previewId, setPreviewId] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        priceHistoryAPI.getAlerts(threshold, 50)
            .then(res => { if (mounted) setAlerts(res.data || []); })
            .catch(() => { if (mounted) setAlerts([]); })
            .finally(() => { if (mounted) setLoading(false); });
        return () => { mounted = false; };
    }, [threshold]);

    const handleThresholdChange = (value) => {
        const v = parseFloat(value);
        if (!isFinite(v) || v < 0) return;
        setThreshold(v);
        localStorage.setItem('priceAlertsThreshold', String(v));
    };

    const visibleAlerts = alerts.filter(a => !dismissed.has(`${a.supplier_product_id}_${a.date}`));
    const shown = expanded ? visibleAlerts : visibleAlerts.slice(0, maxItems);

    const dismiss = (alert) => {
        const key = `${alert.supplier_product_id}_${alert.date}`;
        const next = new Set(dismissed);
        next.add(key);
        setDismissed(next);
        localStorage.setItem('dismissedPriceAlerts', JSON.stringify([...next]));
    };

    if (loading && alerts.length === 0) {
        return (
            <Card data-testid="price-alerts-card">
                <CardContent className="p-6 flex items-center gap-2 text-sm text-slate-400">
                    <Loader2 className="w-4 h-4 animate-spin" /> Chargement des alertes tarifaires…
                </CardContent>
            </Card>
        );
    }

    const headerAccent = visibleAlerts.length > 0;

    return (
        <Card className={headerAccent ? "border-red-200 bg-gradient-to-br from-red-50/60 to-orange-50/40" : ""} data-testid="price-alerts-card">
            <CardHeader
                className="pb-3 cursor-pointer select-none"
                onClick={() => setBodyOpen(o => !o)}
                data-testid="price-alerts-toggle"
            >
                <CardTitle className="flex items-center gap-2 text-base flex-wrap">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${headerAccent ? 'bg-red-100' : 'bg-slate-100'}`}>
                        <AlertTriangle className={`w-4 h-4 ${headerAccent ? 'text-red-600' : 'text-slate-400'}`} />
                    </div>
                    <span>Alertes tarifaires</span>
                    {visibleAlerts.length > 0 && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-bold">
                            {visibleAlerts.length} hausse{visibleAlerts.length > 1 ? 's' : ''}
                        </span>
                    )}
                    <div className="ml-auto flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <label className="text-xs font-normal text-slate-500 flex items-center gap-1.5">
                            Seuil :
                            <input
                                type="number"
                                min="0"
                                step="0.5"
                                value={threshold}
                                onChange={(e) => handleThresholdChange(e.target.value)}
                                className="w-16 px-2 py-1 text-xs font-semibold text-slate-700 bg-white border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-red-300"
                                data-testid="alerts-threshold-input"
                            />
                            <span className="text-slate-500">%</span>
                        </label>
                        {bodyOpen
                            ? <ChevronDown className="w-4 h-4 text-slate-400" />
                            : <ChevronRight className="w-4 h-4 text-slate-400" />}
                    </div>
                </CardTitle>
            </CardHeader>
            {bodyOpen && (
            <CardContent className="pt-0">
                {visibleAlerts.length === 0 ? (
                    <div className="py-4 text-sm text-slate-400 text-center">
                        Aucune hausse ≥ {threshold}% détectée {loading && '(chargement…)'}
                    </div>
                ) : (
                <div className="divide-y divide-red-100">
                    {shown.map((a) => {
                        const key = `${a.supplier_product_id}_${a.date}`;
                        return (
                            <div
                                key={key}
                                className="py-2.5 flex items-center gap-3 cursor-pointer hover:bg-red-50/40 -mx-2 px-2 rounded transition-colors"
                                data-testid={`price-alert-${a.supplier_product_id}`}
                                onClick={() => setPreviewId(a.supplier_product_id)}
                            >
                                <div className="w-9 h-9 rounded bg-white border border-red-100 flex-shrink-0 overflow-hidden flex items-center justify-center">
                                    {a.image_url ? (
                                        <img src={`${process.env.REACT_APP_BACKEND_URL}${a.image_url}`} alt="" className="w-full h-full object-contain" />
                                    ) : (
                                        <TrendingUp className="w-4 h-4 text-red-400" />
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-semibold text-slate-800 text-sm truncate">
                                            {a.industrial_product_name || a.supplier_name_product}
                                        </span>
                                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-red-100 text-red-700 text-[11px] font-bold">
                                            <TrendingUp className="w-3 h-3" />
                                            +{a.variation_pct}%
                                        </span>
                                    </div>
                                    <div className="text-xs text-slate-500 truncate flex items-center gap-1.5">
                                        {a.supplier_name && (
                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-white border border-red-100 text-[10px] font-semibold text-slate-700 flex-shrink-0">
                                                {a.supplier_name}
                                            </span>
                                        )}
                                        <span className="truncate">{a.supplier_name_product}</span>
                                        {a.date && <span className="text-slate-400 flex-shrink-0">· {a.date}</span>}
                                    </div>
                                </div>
                                <div className="text-right flex-shrink-0 hidden sm:block">
                                    <div className="text-xs text-slate-400 line-through">
                                        {formatCurrency(a.previous_price)}
                                    </div>
                                    <div className="text-sm font-bold text-red-600">
                                        {formatCurrency(a.unit_price)}<span className="text-xs text-slate-400 font-normal">/{a.invoice_unit || 'u'}</span>
                                    </div>
                                </div>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="flex-shrink-0 text-slate-400 hover:text-red-600"
                                    onClick={(e) => { e.stopPropagation(); dismiss(a); }}
                                    title="Masquer cette alerte"
                                    data-testid={`dismiss-alert-${a.supplier_product_id}`}
                                >
                                    <X className="w-4 h-4" />
                                </Button>
                            </div>
                        );
                    })}
                </div>
                )}
                <div className={`flex items-center justify-between pt-3 mt-1 border-t ${headerAccent ? 'border-red-100' : 'border-slate-100'}`}>
                    {visibleAlerts.length > maxItems && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setExpanded(!expanded)}
                            data-testid="toggle-alerts-expand"
                        >
                            {expanded ? 'Réduire' : `Voir les ${visibleAlerts.length - maxItems} autre(s)`}
                        </Button>
                    )}
                    <Button
                        variant="ghost"
                        size="sm"
                        className="ml-auto text-red-700 hover:text-red-800"
                        onClick={() => navigate('/products')}
                        data-testid="goto-products-from-alerts"
                    >
                        Voir les produits <ChevronRight className="w-3.5 h-3.5 ml-1" />
                    </Button>
                </div>
            </CardContent>
            )}

            <SupplierProductPreviewDialog
                supplierProductId={previewId}
                onClose={() => setPreviewId(null)}
            />
        </Card>
    );
}
